import { AppRoutes } from "../Constants/AppRoutes";
// import { GetProcessParam } from "../Utils/ProcessParams"; //Fix Warning Complaint Code:071225
import AjaxCache from "./AjaxCacheSystem";
import { AppConfig } from "../Config";

export interface IAPIRequest {
  method: string;
  url: string;
  payload: any;
  headers: any;
  dontStringify: boolean;
  disableTimeout: boolean;
  allowApiCache: boolean;
}

export interface IAPIResponse {
  raw: any;
  parsed: any;
  error: string | null;
}

export interface CustomResponseCodeHandler {
  code: number;
  action: (response: IAPIResponse) => void;
}

export const getBaseUrl = () => {
  return AppConfig.api.baseUrl;
};

const applyDefaultHeaders = (requestHeaders: any) => {
  let defaultHeaders = {
    Authorization: "Bearer " + localStorage.getItem("sessionToken"),
    "Content-Type": "application/json",
  };
  return { ...requestHeaders, ...defaultHeaders };
};

const GENERIC_ERROR: string =
  "Impossibile eseguire l'operazione: il server è irraggiungibile.";
const CustomResponseCodeHandlers: CustomResponseCodeHandler[] = [];

const FetchWithTimeout = async (
  url: string,
  options: any,
  disableTimeout: boolean
) => {
  const { timeout = 15000 } = options;

  // normal fecth call
  if (disableTimeout) {
    try {
      const response = await fetch(url, options);
      return response;
    } catch (e) {
      return {
        statusText: GENERIC_ERROR,
        status: 500,
        json: () => {
          return null;
        },
      };
    }
  }
  // timed out fecth call
  else {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });
      clearTimeout(id);
      return response;
    } catch (e) {
      clearTimeout(id);
      return {
        statusText: GENERIC_ERROR,
        status: 500,
        json: () => {
          return null;
        },
      };
    }
  }
};

// formatter for unreadable responses
const GetStandardErrorResponse = () => {
  return { raw: null, parsed: null, error: GENERIC_ERROR };
};

// utility to get a request object
const GetEmptyRequest = () => {
  return {
    method: "GET",
    url: "",
    payload: null,
    headers: {},
    dontStringify: false,
    disableTimeout: false,
    allowApiCache: false,
  } as IAPIRequest;
};

// this function returns the status text if the status code is >= 300, else null
const GetRequestError = (response: IAPIResponse) => {
  if (response.parsed && response.parsed.message) {
    return response.parsed.message;
  }

  let rawRequest = response.raw;
  if (rawRequest) {
    let rawstatus = rawRequest.status;
    let rawText = rawRequest.statusText;
    if (rawstatus >= 300) {
      return rawText;
    }
  }

  return response.error;
};

const APICall = async (
  apiRequest: IAPIRequest,
  callback?: (response: IAPIResponse) => void
) => {
  // get a response
  let response = await ExecuteAPICall(apiRequest, callback);

  // validate the result
  response.error = GetRequestError(response);

  // this loop handles automatically specific api codes
  for (let i = 0; i < CustomResponseCodeHandlers.length; i++) {
    if (response.raw) {
      if (
        CustomResponseCodeHandlers[i].code === response.raw.status ||
        CustomResponseCodeHandlers[i].code === 0
      ) {
        CustomResponseCodeHandlers[i].action(response);
        break;
      }
    }
  }

  return response;
};

const ExecuteAPICall = async (
  apiRequest: IAPIRequest,
  callback?: (response: IAPIResponse) => void,
  isInternalRetry = false
) => {
  let cachedResponse: IAPIResponse | undefined =
    AjaxCache.GetCachedResponse(apiRequest);

  if (cachedResponse && !isInternalRetry) {
    // this is done to force the API to run so that
    // on the next call the cache will be updated
    setTimeout(() => {
      ExecuteAPICall(apiRequest, callback, true);
    }, 1);
    console.log("API served by cache", apiRequest.url, cachedResponse);
    if (callback) {
      callback(cachedResponse);
    }
    return cachedResponse;
  }

  // set default parameters
  let parameters: any = {
    method: apiRequest.method, // *GET, POST, PUT, DELETE, etc.
    mode: "cors", // no-cors, *cors, same-origin
    cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
    credentials: "same-origin", // include, *same-origin, omit
    redirect: "follow", // manual, *follow, error
    referrerPolicy: "no-referrer", // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
  };

  // set payload if exists
  if (apiRequest.payload) {
    if (apiRequest.dontStringify) {
      parameters["body"] = apiRequest.payload; // e.g: for file upload
    } else {
      parameters["body"] = JSON.stringify(apiRequest.payload); // body data type must match "Content-Type" header
    }
  }

  // add custom headers, if any, else use the classic application/json header
  parameters["headers"] = applyDefaultHeaders(apiRequest.headers);

  // do the fetch call
  let baseUrl = getBaseUrl();

  const response = await FetchWithTimeout(
    baseUrl + apiRequest.url,
    parameters,
    apiRequest.disableTimeout
  );

  let parsedResponse: IAPIResponse | null = null;

  if (
    response.status === 401 &&
    !window.location.pathname.includes(AppRoutes.APP_LOGIN_PAGE)
  ) {
    (window as any)["expiredSession"]();
  }
  // try read the response
  try {
    parsedResponse = {
      raw: response,
      parsed: await response.json(),
      error: null,
    };
  } catch (e) {
    console.error((e as any).message);
    parsedResponse = GetStandardErrorResponse();
  }

  // utility dev print
  let developerLog: any = { ...parameters, url: apiRequest.url };
  developerLog["response"] = parsedResponse;
  console.log("API", apiRequest.url, developerLog);

  AjaxCache.WriteResponseToCache(apiRequest, parsedResponse);

  if (callback) {
    callback(parsedResponse);
  }
  return parsedResponse;
};

export const AjaxService = {
  GetEmptyRequest,
  CustomResponseCodeHandlers,
  APICall,
};
